import {
  TagPickerInput as FluentTagPickerInput,
  TagPickerInputProps as FluentTagPickerInputProps,
} from '@fluentui/react-components';
import React, { KeyboardEvent, useCallback } from 'react';
import { focusTagPickerInput } from './focusTagPickerInput';

export interface TagPickerInputProps extends FluentTagPickerInputProps {
  /**
   * Called when Backspace is pressed while the input is empty — the consumer
   * should remove the last selected tag. Fluent v9 only moves focus to the tag
   * here; the actual removal is not wired, so this centralizes that behaviour
   * (previously hand-rolled as an `onKeyDown` handler in every consumer).
   */
  onRemoveLast?: () => void;
}

/**
 * TagPickerInput with built-in "Backspace on empty input removes the last tag"
 * behaviour. Any `onKeyDown` passed by the consumer still runs first (and can
 * `preventDefault()` to opt out of the removal for a given key press).
 */
export const TagPickerInput: React.FC<TagPickerInputProps> = ({
  onRemoveLast,
  onKeyDown,
  ...props
}) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);

      if (
        onRemoveLast &&
        !event.defaultPrevented &&
        event.key === 'Backspace' &&
        event.currentTarget.value === ''
      ) {
        const control = event.currentTarget.closest('.fui-TagPickerControl');
        event.preventDefault();
        onRemoveLast();
        // Removing the last tag re-creates the input and drops focus; restore it.
        focusTagPickerInput(control);
      }
    },
    [onKeyDown, onRemoveLast],
  );

  return <FluentTagPickerInput {...props} onKeyDown={handleKeyDown} />;
};

TagPickerInput.displayName = 'TagPickerInput';
